import { useCallback, useEffect, useRef, useState } from 'react';
import fixWebmDuration from 'webm-duration-fix';
import { setupVolumeProcessor } from './volumeProcessor';
import { getCoPilotAuthToken } from '@/services/BackendAPI';
import { trackTranscriptionReconnectAttempt, trackTranscriptionStarted } from '@/services/analytics';
import { removeFillers } from '@/utils/transcriptReplacements';

type UseAudioRecorderReturn = {
  startRecording: (device: MediaDeviceInfo) => void;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  resetRecorder: () => void;
  toggleRecording: (device: MediaDeviceInfo) => void;
  audioBlob: Blob | null;
  recordingTime: number;
  state: 'idle' | 'initializing' | 'recording' | 'pausing' | 'paused' | 'stopped';
  dictationError?: string;
};

export const useStreamedSpeechRec = (addDictation: {
  (dictation: { text: string; isFinal: boolean }): void;
}): UseAudioRecorderReturn => {
  const TRANSCRIPTION_FAILURE_MESSAGE =
    'Talegenkendelsen fungerer desværre ikke i øjeblikket, men din diktat vil stadig blive optaget. Tjek evt. din internetforbindelse.';

  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [state, setState] = useState<
    'idle' | 'initializing' | 'recording' | 'pausing' | 'paused' | 'stopped'
  >('idle');
  const [time, setTime] = useState<number>(0);

  const blobRecorderRef = useRef<MediaRecorder | null>(null);
  const transcribeRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const displayMediaStreamRef = useRef<MediaStream | null>(null);
  const websocketRef = useRef<WebSocket | null>(null);
  const dictationError = useRef<string | null>(null);
  const stateRef = useRef<typeof state>('idle');
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<number | null>(null);
  const lastSocketFailureReasonRef = useRef<string | null>(null);
  const pauseTimeoutRef = useRef<number | null>(null);
  const waitingForFlushPauseRef = useRef(false);
  const isReconnectingRef = useRef(false);
  const isIntentionalCloseRef = useRef(false);
  const websocketGenerationRef = useRef(0);
  const activeSocketRef = useRef<WebSocket | null>(null);
  const activeSocketGenerationRef = useRef(0);
  const transcribeRecorderGenerationRef = useRef(0);
  const pendingTranscribeChunksRef = useRef<Blob[]>([]);
  const reconnectTimerResolveRef = useRef<(() => void) | null>(null);
  const isInterimModeRef = useRef(true);
  const lastInterimTranscriptRef = useRef('');
  const lastInterimCommittedOnDisconnectRef = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (reconnectTimerResolveRef.current) {
      reconnectTimerResolveRef.current();
      reconnectTimerResolveRef.current = null;
    }
  };

  const clearPauseTimeout = () => {
    if (pauseTimeoutRef.current !== null) {
      window.clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
  };

  const describeCloseEvent = (event: CloseEvent) => {
    const reason = event.reason?.trim() ? event.reason : 'no reason';
    return `close code=${event.code}, wasClean=${event.wasClean}, reason="${reason}"`;
  };

  const stopRecorder = (recorder: MediaRecorder | null) => {
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  };

  const stopTranscribeRecorder = () => {
    if (transcribeRecorderRef.current) {
      transcribeRecorderRef.current.ondataavailable = null;
    }
    stopRecorder(transcribeRecorderRef.current);
    transcribeRecorderRef.current = null;
  };

  const closeWebSocket = () => {
    activeSocketRef.current = null;
    activeSocketGenerationRef.current = 0;
    if (
      websocketRef.current &&
      websocketRef.current.readyState !== WebSocket.CLOSING &&
      websocketRef.current.readyState !== WebSocket.CLOSED
    ) {
      websocketRef.current.close();
    }
    if (websocketRef.current) {
      websocketRef.current = null;
    }
  };

  const waitForWebSocketClose = (socket: WebSocket, timeoutMs: number) =>
    new Promise<void>((resolve) => {
      if (socket.readyState === WebSocket.CLOSED) {
        resolve();
        return;
      }

      let settled = false;
      let timeoutId: number | null = null;

      const finish = () => {
        if (settled) {
          return;
        }
        settled = true;
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        socket.removeEventListener('close', onClose);
        socket.removeEventListener('error', onError);
        resolve();
      };

      const onClose = () => {
        finish();
      };

      const onError = () => {
        finish();
      };

      socket.addEventListener('close', onClose);
      socket.addEventListener('error', onError);
      timeoutId = window.setTimeout(() => {
        console.warn(
          'Timed out waiting for server to close transcription websocket after %dms',
          timeoutMs,
        );
        finish();
      }, timeoutMs);
    });

  const buildTranscribeRecorder = (stream: MediaStream) => {
    stopTranscribeRecorder();
    transcribeRecorderGenerationRef.current += 1;
    const recorderGeneration = transcribeRecorderGenerationRef.current;
    const recorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus',
    });
    recorder.ondataavailable = (event) => {
      if (event.data.size === 0 || recorderGeneration !== transcribeRecorderGenerationRef.current) {
        return;
      }
      const activeSocket = activeSocketRef.current;
      if (
        activeSocket &&
        activeSocket.readyState === WebSocket.OPEN &&
        activeSocketGenerationRef.current === websocketGenerationRef.current
      ) {
        activeSocket.send(event.data);
        return;
      }
      const MAX_PENDING_CHUNKS = 192; // ~48s at 250ms intervals
      const pending = pendingTranscribeChunksRef.current;
      if (pending.length >= MAX_PENDING_CHUNKS) {
        console.error(
          'Transcription buffer overflowed (%d chunks) — giving up on transcription for this session.',
          MAX_PENDING_CHUNKS,
        );
        lastSocketFailureReasonRef.current = `buffer overflow at ${MAX_PENDING_CHUNKS} chunks`;
        dictationError.current = TRANSCRIPTION_FAILURE_MESSAGE;
        isIntentionalCloseRef.current = true;
        pendingTranscribeChunksRef.current = [];
        stopTranscribeRecorder();
        closeWebSocket();
        return;
      }
      pendingTranscribeChunksRef.current.push(event.data);
    };
    recorder.start(250);
    transcribeRecorderRef.current = recorder;
  };

  const rotateTranscribeRecorderForBuffering = () => {
    if (
      isIntentionalCloseRef.current ||
      stateRef.current !== 'recording' ||
      !mediaStreamRef.current
    ) {
      return;
    }
    activeSocketRef.current = null;
    activeSocketGenerationRef.current = 0;
    buildTranscribeRecorder(mediaStreamRef.current);
  };

  const flushPendingTranscribeChunks = (socket: WebSocket, socketGeneration: number) => {
    if (
      socket !== websocketRef.current ||
      activeSocketRef.current !== socket ||
      activeSocketGenerationRef.current !== socketGeneration
    ) {
      return;
    }
    const queuedChunks = [...pendingTranscribeChunksRef.current];
    if (queuedChunks.length === 0) {
      return;
    }
    let flushedCount = 0;
    for (const chunk of queuedChunks) {
      if (
        socket !== websocketRef.current ||
        activeSocketRef.current !== socket ||
        activeSocketGenerationRef.current !== socketGeneration ||
        socket.readyState !== WebSocket.OPEN
      ) {
        break;
      }
      socket.send(chunk);
      flushedCount += 1;
    }
    if (flushedCount > 0) {
      pendingTranscribeChunksRef.current = pendingTranscribeChunksRef.current.slice(flushedCount);
    }
  };

  const commitLastInterimIfNeeded = () => {
    if (
      isIntentionalCloseRef.current ||
      !isInterimModeRef.current ||
      stateRef.current !== 'recording' ||
      lastInterimCommittedOnDisconnectRef.current
    ) {
      return;
    }

    const lastInterim = lastInterimTranscriptRef.current.trim();
    if (!lastInterim) {
      return;
    }

    addDictation({ text: lastInterim, isFinal: true });
    lastInterimCommittedOnDisconnectRef.current = true;
    lastInterimTranscriptRef.current = '';
  };

  const finalizePauseIfNeeded = () => {
    if (stateRef.current !== 'pausing') {
      waitingForFlushPauseRef.current = false;
      clearPauseTimeout();
      return;
    }

    if (blobRecorderRef.current?.state === 'recording') {
      blobRecorderRef.current.pause();
    }
    if (transcribeRecorderRef.current?.state === 'recording') {
      transcribeRecorderRef.current.pause();
    }
    if (
      blobRecorderRef.current?.state === 'paused' ||
      transcribeRecorderRef.current?.state === 'paused'
    ) {
      setState('paused');
      mediaStreamRef.current?.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
    }
    waitingForFlushPauseRef.current = false;
    clearPauseTimeout();
  };

  const handleSocketAccepted = (socket: WebSocket, socketGeneration: number, sessionId: string) => {
    reconnectAttemptsRef.current = 0;
    lastSocketFailureReasonRef.current = null;
    isReconnectingRef.current = false;
    activeSocketRef.current = socket;
    activeSocketGenerationRef.current = socketGeneration;
    flushPendingTranscribeChunks(socket, socketGeneration);
    if (!transcribeRecorderRef.current && mediaStreamRef.current) {
      buildTranscribeRecorder(mediaStreamRef.current);
    }
    trackTranscriptionStarted({ sessionId });
  };

  const sendSocketConfig = (socket: WebSocket) => {
    const language = new URLSearchParams(window.location.search).get('lang') || 'da';
    const interimResults = true;
    socket.send(
      JSON.stringify({
        type: 'config',
        configuration: {
          primaryLanguage: language,
          spokenPunctuation: false,
          automaticPunctuation: true,
          interimResults: interimResults,
        },
      }),
    );
  };

  const handleSocketMessage = (
    socket: WebSocket,
    socketGeneration: number,
    message: {
      type: string;
      data?: { text: string; isFinal: boolean };
      sessionId?: string;
      error?: unknown;
    },
  ) => {
    switch (message.type) {
      case 'CONFIG_ACCEPTED':
        handleSocketAccepted(socket, socketGeneration, message.sessionId ?? '');
        break;
      case 'CONFIG_DENIED':
        console.error('CONFIG_DENIED', message);
        lastSocketFailureReasonRef.current = `CONFIG_DENIED: ${String(message.error ?? 'unknown')}`;
        isIntentionalCloseRef.current = true;
        stopTranscribeRecorder();
        pendingTranscribeChunksRef.current = [];
        closeWebSocket();
        // Keep blobRecorderRef running so the audio is still recorded locally
        // even though transcription is unavailable.
        dictationError.current = TRANSCRIPTION_FAILURE_MESSAGE;
        break;
      case 'transcript':
        if (message.data) {
          const cleaned = { ...message.data, text: removeFillers(message.data.text) };
          if (cleaned.isFinal) {
            lastInterimTranscriptRef.current = '';
            lastInterimCommittedOnDisconnectRef.current = false;
          } else if (typeof cleaned.text === 'string') {
            lastInterimTranscriptRef.current = cleaned.text;
          }
          addDictation(cleaned);
        }
        break;
      case 'ended':
        isIntentionalCloseRef.current = true;
        stopTranscribeRecorder();
        closeWebSocket();
        break;
      case 'flushed':
        if (waitingForFlushPauseRef.current) {
          finalizePauseIfNeeded();
        }
        break;
      case 'error':
        console.error('Transcription socket error', message.error ?? message);
        lastSocketFailureReasonRef.current = `server error: ${String(message.error ?? 'unknown')}`;
        commitLastInterimIfNeeded();
        stopTranscribeRecorder();
        closeWebSocket();
        rotateTranscribeRecorderForBuffering();
        break;
      default:
        console.warn('Unhandled message type: %s', message.type, message);
        break;
    }
  };

  const attachSocketHandlers = (socket: WebSocket, socketGeneration: number) => {
    socket.onopen = () => {
      if (socket !== websocketRef.current || websocketGenerationRef.current !== socketGeneration) {
        return;
      }
      sendSocketConfig(socket);
    };

    socket.onmessage = (event) => {
      if (socket !== websocketRef.current || websocketGenerationRef.current !== socketGeneration) {
        return;
      }
      const message = JSON.parse(event.data);
      handleSocketMessage(socket, socketGeneration, message);
    };

    socket.onclose = (event) => {
      if (!isIntentionalCloseRef.current) {
        lastSocketFailureReasonRef.current = describeCloseEvent(event);
        console.warn(
          'Transcription websocket closed unexpectedly: %s (readyState=%d, generation=%d, reconnectAttempts=%d)',
          describeCloseEvent(event),
          socket.readyState,
          socketGeneration,
          reconnectAttemptsRef.current,
        );
      }
      if (socket === websocketRef.current) {
        websocketRef.current = null;
      }
      if (socketGeneration === websocketGenerationRef.current) {
        isReconnectingRef.current = false;
        commitLastInterimIfNeeded();
        rotateTranscribeRecorderForBuffering();
        scheduleReconnect();
      }
    };

    socket.onerror = (event) => {
      if (isIntentionalCloseRef.current) {
        return; // do not log error if intentional close
      }
      lastSocketFailureReasonRef.current = 'websocket error event';
      console.error('Transcription websocket error event:', {
        event,
        readyState: socket.readyState,
        socketGeneration,
        reconnectAttempts: reconnectAttemptsRef.current,
      });
      // onclose always fires after onerror — reconnect logic lives there.
    };
  };

  const shouldAttemptReconnect = () =>
    !isIntentionalCloseRef.current &&
    stateRef.current === 'recording' &&
    Boolean(mediaStreamRef.current);

  const scheduleReconnect = async () => {
    if (!shouldAttemptReconnect()) {
      return;
    }

    if (isReconnectingRef.current) {
      return;
    }
    isReconnectingRef.current = true;

    const maxReconnectAttempts = 5;
    const attempt = reconnectAttemptsRef.current + 1;
    reconnectAttemptsRef.current = attempt;
    if (attempt > maxReconnectAttempts) {
      console.error(
        'Giving up reconnecting transcription websocket after %d attempts. Last known reason: %s',
        maxReconnectAttempts,
        lastSocketFailureReasonRef.current ?? 'unknown',
      );
      dictationError.current = TRANSCRIPTION_FAILURE_MESSAGE;
      pendingTranscribeChunksRef.current = [];
      isReconnectingRef.current = false;
      return;
    }

    const delayMs = Math.min(1000 * 2 ** (attempt - 1), 10000);
    trackTranscriptionReconnectAttempt(attempt);
    console.warn(
      'Transcription websocket reconnect attempt %d/%d in %dms. Last known reason: %s',
      attempt,
      maxReconnectAttempts,
      delayMs,
      lastSocketFailureReasonRef.current ?? 'unknown',
    );
    await new Promise<void>((resolve) => {
      reconnectTimerResolveRef.current = resolve;
      reconnectTimerRef.current = window.setTimeout(resolve, delayMs);
    });
    reconnectTimerRef.current = null;
    reconnectTimerResolveRef.current = null;

    if (!shouldAttemptReconnect()) {
      isReconnectingRef.current = false;
      return;
    }

    try {
      const copilotToken = await getCoPilotAuthToken();
      const socketGeneration = websocketGenerationRef.current + 1;
      const socket = new WebSocket(
        `wss://api.eu.corti.app/audio-bridge/v2/transcribe?token=Bearer%20${copilotToken.accessToken}&tenant-name=${copilotToken.tenant}`,
      );

      websocketGenerationRef.current = socketGeneration;
      websocketRef.current = socket;
      attachSocketHandlers(socket, socketGeneration);
    } catch (error) {
      console.error('Failed to reconnect websocket:', error);
      lastSocketFailureReasonRef.current = `reconnect exception: ${String(error)}`;
      isReconnectingRef.current = false;
      scheduleReconnect();
    }
  };

  // If recording, update the volume
  useEffect(() => {
    let cleanupAudio: () => void;
    let initialTimer: NodeJS.Timeout | null = null;
    let audioDetected = false;

    if (state === 'recording' && mediaStreamRef.current) {
      // After 10 seconds, if no audio has been detected, set the error.
      initialTimer = setTimeout(() => {
        if (!audioDetected) {
          dictationError.current = 'Ingen lyd registreret. Kontroller din mikrofon, og prøv igen.';
          document.documentElement.style.setProperty('--volume-error-outline', '8px solid red');
        }
      }, 10000);

      cleanupAudio = setupVolumeProcessor(mediaStreamRef.current, (normalizedVolume) => {
        document.documentElement.style.setProperty(
          '--volume-scale',
          `${1.2 * normalizedVolume + 1}`,
        );
        document.documentElement.style.setProperty('--volume-opacity', `${normalizedVolume * 0.5}`);

        // If audio is detected, clear error and cancel the timer.
        if (normalizedVolume > 0.1) {
          if (!audioDetected) {
            audioDetected = true;
            if (
              dictationError.current ===
              'Ingen lyd registreret. Kontroller din mikrofon, og prøv igen.'
            ) {
              dictationError.current = null;
            }
            document.documentElement.style.setProperty('--volume-error-outline', 'none');
            if (initialTimer) {
              clearTimeout(initialTimer);
              initialTimer = null;
            }
          }
        }
      });
    } else {
      document.documentElement.style.setProperty('--volume-scale', '1');
      document.documentElement.style.setProperty('--volume-opacity', '0');
      document.documentElement.style.setProperty('--volume-error-outline', 'none');
      if (initialTimer) clearTimeout(initialTimer);
    }

    return () => {
      if (cleanupAudio) cleanupAudio();
      if (initialTimer) clearTimeout(initialTimer);
    };
  }, [state, mediaStreamRef]);

  useEffect(() => {
    if (state === 'recording') {
      const intervalId = setInterval(() => {
        setTime((prev) => prev + 1);
      }, 1000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [state]);

  const startRecording = async (device?: MediaDeviceInfo) => {
    setState('initializing');
    isIntentionalCloseRef.current = false;
    isReconnectingRef.current = false;
    waitingForFlushPauseRef.current = false;
    dictationError.current = null;
    lastSocketFailureReasonRef.current = null;
    clearReconnectTimer();
    clearPauseTimeout();
    reconnectAttemptsRef.current = 0;
    lastInterimTranscriptRef.current = '';
    lastInterimCommittedOnDisconnectRef.current = false;
    pendingTranscribeChunksRef.current = [];
    activeSocketRef.current = null;
    activeSocketGenerationRef.current = 0;
    stopRecorder(blobRecorderRef.current);
    blobRecorderRef.current = null;
    stopTranscribeRecorder();
    closeWebSocket();

    // Create a new media stream
    const constraints = {
      audio: {
        deviceId: device?.deviceId ? { exact: device?.deviceId } : undefined,
        voiceIsolation: true,
      },
    };

    // wait until websocket is open before getting the media stream

    // if url contains query param 'display', get display media instead of audio (for debugging purposes)
    if (window.location.search.includes('display')) {
      displayMediaStreamRef.current = await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: true,
      });
      // get the audio track only
      mediaStreamRef.current = new MediaStream();
      displayMediaStreamRef.current.getAudioTracks().forEach((track) => {
        mediaStreamRef.current?.addTrack(track);
      });
    } else {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia(constraints);
    }

    // Recorder used only for final blob — started immediately so the full
    // audio is captured as a single file regardless of transcription state.
    audioChunksRef.current = [];
    blobRecorderRef.current = new MediaRecorder(mediaStreamRef.current, {
      mimeType: 'audio/webm;codecs=opus',
    });

    blobRecorderRef.current.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    blobRecorderRef.current.onstop = () => {
      const chunks = audioChunksRef.current;
      audioChunksRef.current = [];
      if (chunks.length > 0) {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        fixWebmDuration(blob).then((fixedBlob) => {
          setAudioBlob(fixedBlob);
        });
      }
    };

    blobRecorderRef.current.start(250);

    // Start the transcribe recorder immediately so it captures audio in
    // lockstep with the blob recorder. Chunks produced before the websocket
    // is accepted are buffered in pendingTranscribeChunksRef and flushed by
    // handleSocketAccepted once CONFIG_ACCEPTED arrives.
    buildTranscribeRecorder(mediaStreamRef.current);

    if (blobRecorderRef.current.state === 'recording') {
      setState('recording');
    }

    try {
      const copilotToken = await getCoPilotAuthToken();
      const socketGeneration = websocketGenerationRef.current + 1;
      const socket = new WebSocket(
        `wss://api.eu.corti.app/audio-bridge/v2/transcribe?token=Bearer%20${copilotToken.accessToken}&tenant-name=${copilotToken.tenant}`,
      );
      websocketGenerationRef.current = socketGeneration;
      websocketRef.current = socket;
      attachSocketHandlers(socket, socketGeneration);
    } catch (error) {
      console.error('Failed to get CoPilot auth token:', error);
      lastSocketFailureReasonRef.current = `token fetch error: ${String(error)}`;
      dictationError.current = TRANSCRIPTION_FAILURE_MESSAGE;
      // Token fetch / socket setup failed — stop the transcribe recorder so it
      // doesn't keep encoding audio for a session that will never transcribe.
      stopTranscribeRecorder();
      pendingTranscribeChunksRef.current = [];
    }
  };

  const stopRecording = async () => {
    isIntentionalCloseRef.current = true;
    clearReconnectTimer();
    clearPauseTimeout();
    waitingForFlushPauseRef.current = false;
    isReconnectingRef.current = false;
    lastInterimTranscriptRef.current = '';
    lastInterimCommittedOnDisconnectRef.current = false;
    pendingTranscribeChunksRef.current = [];
    activeSocketRef.current = null;
    activeSocketGenerationRef.current = 0;
    stopTranscribeRecorder();

    const socket = websocketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'end' }));
      await waitForWebSocketClose(socket, 10000);
    }
    closeWebSocket();

    if (blobRecorderRef.current) {
      stopRecorder(blobRecorderRef.current);
      // Blob is built in the onstop handler to ensure the final chunk is included.
      blobRecorderRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setState('stopped');

    if (displayMediaStreamRef.current) {
      displayMediaStreamRef.current.getTracks().forEach((track) => track.stop());
      displayMediaStreamRef.current = null;
    }

    return new Promise((resolve) => {
      resolve(true);
    });
  };

  const pauseRecording = () => {
    // wait 3 seconds before pausing
    setState('pausing');
    waitingForFlushPauseRef.current = true;

    // Flush any buffered audio to Corti before pausing the microphone stream.
    if (transcribeRecorderRef.current?.state === 'recording') {
      try {
        transcribeRecorderRef.current.requestData();
      } catch (error) {
        console.warn('Failed to request transcribe recorder data before flush', error);
      }
    }

    if (activeSocketRef.current?.readyState === WebSocket.OPEN) {
      activeSocketRef.current.send(JSON.stringify({ type: 'flush' }));
    }

    mediaStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = false;
    });

    // Here's why we need to wait 3 seconds before pausing: https://github.com/corticph/code-frontend/pull/683
    clearPauseTimeout();
    pauseTimeoutRef.current = window.setTimeout(() => {
      finalizePauseIfNeeded();
    }, 3000);
  };

  const resumeRecording = () => {
    clearPauseTimeout();
    waitingForFlushPauseRef.current = false;
    if (blobRecorderRef.current?.state === 'paused') {
      blobRecorderRef.current.resume();
    }
    if (transcribeRecorderRef.current?.state === 'paused') {
      transcribeRecorderRef.current.resume();
    }
    if (
      blobRecorderRef.current?.state === 'recording' ||
      transcribeRecorderRef.current?.state === 'recording'
    ) {
      setState('recording');
    }
    if (
      !isIntentionalCloseRef.current &&
      !websocketRef.current &&
      !isReconnectingRef.current &&
      mediaStreamRef.current
    ) {
      scheduleReconnect();
    }
  };

  const resetRecorder = () => {
    isIntentionalCloseRef.current = true;
    clearReconnectTimer();
    clearPauseTimeout();
    waitingForFlushPauseRef.current = false;
    isReconnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    lastSocketFailureReasonRef.current = null;
    lastInterimTranscriptRef.current = '';
    lastInterimCommittedOnDisconnectRef.current = false;
    pendingTranscribeChunksRef.current = [];
    activeSocketRef.current = null;
    activeSocketGenerationRef.current = 0;
    stopRecorder(blobRecorderRef.current);
    blobRecorderRef.current = null;
    stopTranscribeRecorder();

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    closeWebSocket();

    setState('idle');
    dictationError.current = null;
  };

  const toggleRecording = useCallback(
    (device?: MediaDeviceInfo) => {
      if (state === 'recording') {
        pauseRecording();
      } else if (state === 'paused') {
        resumeRecording();
      } else {
        startRecording(device);
      }
    },
    [state],
  );

  return {
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    resetRecorder,
    toggleRecording,
    audioBlob,
    state,
    recordingTime: time,
    dictationError: dictationError.current,
  };
};
